import { supabase } from '../lib/supabase';
import { plans } from '../config/plans'; // Import plans

interface CheckoutOptions {
  priceId: string;
  userId: string;
}

export class StripeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'StripeError';
  }
}

// New function to insert order into Supabase
async function insertOrder(userId: string, productName: string, pricePaid: number, stripeSessionId: string) {
  console.log('Inserting order with:', { userId, productName, pricePaid, stripeSessionId }); // Debugging log
  const { data, error } = await supabase
    .from('orders')
    .insert([
      {
        user_id: userId,
        product_name: productName,
        price_paid: pricePaid,
        stripe_session_id: stripeSessionId,
      },
    ]);

  if (error) {
    console.error('Insert error:', error); // Log the error
    throw new Error(`Failed to insert order: ${error.message}`);
  }

  console.log('Order inserted successfully:', data); // Log success
  return data;
}

export async function initiateCheckout({ priceId, userId }: CheckoutOptions): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) {
      throw new StripeError('Authentication required');
    }

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/create-checkout`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({ priceId, userId }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new StripeError(error.error || 'Checkout creation failed');
    }

    const { url, sessionId } = await response.json(); 
    if (!url) {
      throw new StripeError('Invalid checkout response');
    }

    // Find the corresponding plan based on priceId
    const selectedPlan = plans.find(plan => plan.stripeProductId === priceId);
    if (!selectedPlan) {
      throw new StripeError('Invalid price ID');
    }

    // Insert order into Supabase
    const productName = selectedPlan.name; // Get product name from selected plan
    const pricePaid = parseFloat(selectedPlan.price); // Get price from selected plan

    try {
      await insertOrder(userId, productName, pricePaid, sessionId); // Call insertOrder
    } catch (insertError) {
      console.error('Order insertion error:', insertError);
      throw new StripeError('Failed to record order. Please try again.'); // Prevent redirect
    }

    // Redirect to Stripe Checkout in the same window
    window.location.href = url;
  } catch (error) {
    console.error('Stripe checkout error:', error);
    throw error instanceof StripeError 
      ? error 
      : new StripeError('Failed to initiate checkout');
  }
}